import { useMemo, useState } from 'react';
import { taskApi, timeApi, type Task } from '../../api';

// Two-button modal that pops after the user clicks "Complete" on a task.
// Supports MULTIPLE time slots in a single completion (08:30–11:45 + 15:30–18:00),
// each posted as its own time_entry — invoices then render explicit "08:31–09:02"
// rather than just a duration. The advocate's day is rarely one continuous block.
//
// On save: post N time entries, then complete the task.
// On skip: just complete the task.
// Non-billable tasks should not open this modal at all — the parent should call
// taskApi.complete() directly. This component assumes the task is billable.

type Props = {
  task: Task;
  onClose: () => void;
  onCompleted: () => void;
};

type Slot = { start: string; end: string };

function hoursBetween(start: string, end: string): number {
  if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const diff = (eh * 60 + em) - (sh * 60 + sm);
  return diff > 0 ? diff / 60 : 0;
}

const ACTIVITY_CODES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'research',     label: 'Research' },
  { value: 'drafting',     label: 'Drafting' },
  { value: 'appearance',   label: 'Appearance' },
  { value: 'travel',       label: 'Travel' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'perusal',      label: 'Perusal' },
];

export default function CompleteTaskModal({ task, onClose, onCompleted }: Props) {
  const [workDate, setWorkDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [activity, setActivity] = useState<string>('drafting');
  const [description, setDescription] = useState<string>(task.name);
  const [slots, setSlots] = useState<Slot[]>([{ start: '', end: '' }]);
  const [busy, setBusy] = useState<'save' | 'skip' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const totalHours = useMemo(
    () => slots.reduce((sum, s) => sum + hoursBetween(s.start, s.end), 0),
    [slots],
  );

  const updateSlot = (i: number, patch: Partial<Slot>) => {
    setSlots((cur) => cur.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  };
  const addSlot    = () => setSlots((cur) => [...cur, { start: '', end: '' }]);
  const removeSlot = (i: number) => setSlots((cur) => cur.length > 1 ? cur.filter((_, idx) => idx !== i) : cur);

  const saveAndComplete = async () => {
    const valid = slots.filter((s) => hoursBetween(s.start, s.end) > 0);
    if (!valid.length) { setError('Add at least one valid time slot (start before end).'); return; }
    if (!description.trim()) { setError('A short description is required.'); return; }
    setBusy('save'); setError(null);
    try {
      // Each slot posts as its own time_entry. Invoice rendering will pick up
      // start_time/end_time and show explicit ranges per line.
      for (const s of valid) {
        await timeApi.create({
          matter_id: task.matter_id,
          work_date: workDate,
          start_time: s.start,
          end_time: s.end,
          duration_hours: hoursBetween(s.start, s.end),
          activity_code: activity || undefined,
          description: description.trim(),
          task_id: task.ROWID,
          billable: true,
        });
      }
      await taskApi.complete(task.ROWID);
      onCompleted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  const skipAndComplete = async () => {
    setBusy('skip'); setError(null);
    try {
      await taskApi.complete(task.ROWID);
      onCompleted();
    } catch (e) {
      setError((e as Error).message);
      setBusy(null);
    }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modal} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Log time for this task?</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
          {task.name}
        </p>

        <div className="stack">
          <div className="row">
            <div className="grow">
              <label>Work date</label>
              <input
                type="date"
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            </div>
            <div className="grow">
              <label>Activity</label>
              <select value={activity} onChange={(e) => setActivity(e.target.value)}>
                {ACTIVITY_CODES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label>Time slots (08:30–11:45 + 15:30–18:00 = two slots)</label>
            <div className="stack" style={{ gap: 6 }}>
              {slots.map((s, i) => {
                const hrs = hoursBetween(s.start, s.end);
                return (
                  <div key={i} className="row" style={{ alignItems: 'center', gap: 8 }}>
                    <input
                      type="time"
                      value={s.start}
                      onChange={(e) => updateSlot(i, { start: e.target.value })}
                      style={{ width: 120 }}
                    />
                    <span className="muted">–</span>
                    <input
                      type="time"
                      value={s.end}
                      onChange={(e) => updateSlot(i, { end: e.target.value })}
                      style={{ width: 120 }}
                    />
                    <span className="muted" style={{ fontSize: 12, minWidth: 60 }}>
                      {hrs > 0 ? `${hrs.toFixed(2)} h` : '—'}
                    </span>
                    <button
                      className="ghost"
                      onClick={() => removeSlot(i)}
                      disabled={slots.length === 1}
                      style={{ padding: '4px 10px', fontSize: 12 }}
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              <button className="ghost" onClick={addSlot} style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 12 }}>
                + Add another slot
              </button>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Total: <b>{totalHours.toFixed(2)} hours</b>
              </div>
            </div>
          </div>

          <div>
            <label>Description (appears on the invoice)</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={task.name}
            />
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button onClick={onClose} disabled={busy !== null}>Cancel</button>
            <button onClick={skipAndComplete} disabled={busy !== null}>
              {busy === 'skip' ? '…' : 'Skip & complete'}
            </button>
            <button className="primary" onClick={saveAndComplete} disabled={busy !== null}>
              {busy === 'save' ? '…' : 'Log time & complete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modal: React.CSSProperties = { width: 580, maxWidth: '90vw' };
